import { Grid, IconButton, Table, TableBody
    , TableCell, TableRow, Tooltip, Typography, Box } from '@mui/material'
import { ArrowDownward as IconArrowDown } from "@mui/icons-material";
import api from "../services/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Plan de Terapia de la sesion: listado de solo lectura de las areas del plan,
// entre los datos basicos y el detalle de criterios.
//
// Se arma asi: el plan de la terapia cuyas fechas (inicio/fin) coinciden con
// las del periodo de la sesion.
//
// Los dos indicadores de arriba (Detallar Objetivo y Detallar Criterio) son los
// del plan de terapia: se muestran en solo lectura porque los cambia el
// mantenimiento del plan, no la sesion. Ademas se avisan al padre para que el
// detalle de criterios no muestre los campos que el plan no pide, y lo heredan
// los criterios que se crean con la flecha hacia abajo de cada fila.
const SessionPlanArea = (props) => {
    const isReadOnly = props.isReadOnly;
    // Fila del detalle del plan de la que se toma el modelo y el area.
    const alAgregarCriterio = props.onAgregarCriterio;
    const [rows, setRows] = useState([]);
    const [cargando, setCargando] = useState(false);
    // Cada peticion se anota con un numero: si al volver llego otra peticion
    // (cambio de terapia o de periodo), la respuesta vieja se descarta. Sin
    // esto el listado muestra las areas de todos los planes de la terapia, no
    // solo las del periodo de la sesion, y los indicadores quedan mal.
    const corrida = useRef(0);

    const fetchRows = useCallback(async (idTerapia, idPeriodo) => {
        const ticket = ++corrida.current;
        const misma = () => corrida.current === ticket;

        if (!idTerapia || idTerapia <= 0) {
            setRows([]);
            setCargando(false);
            return;
        }

        setCargando(true);

        const response = await api.get("/TerapiaPlan/GetsTerapiaPlanAreaPorTerapia", {
            params: { idTerapia: idTerapia, idPeriodo: idPeriodo == null ? 0 : idPeriodo }
        })
            .catch((err) => {
                console.log("Err: ", err);
                return { data: { data: [] } };
            });

        const lista = response?.data?.data || [];
        if (!misma()) return;

        // Cada fila lleva las areas de su modelo: son el filtro inicial del
        // autocompletado de Area del criterio que se cree con la flecha.
        const modelos = {};
        await Promise.all(lista.map(async (row) => {
            if (modelos[row.idModelo] != null) {
                row.areaModelo = modelos[row.idModelo];
                return;
            }

            const res = await api.get("/Modelo/GetsArea?idModelo=" + row.idModelo)
                .catch((err) => {
                    console.log("Err: ", err);
                    return { data: { data: [] } };
                });

            modelos[row.idModelo] = res?.data?.data || [];
            row.areaModelo = modelos[row.idModelo];
        }));

        if (!misma()) return;

        setRows(lista);
        setCargando(false);

        // Escala de avance del plan: es la que define los grupos de puntuacion
        // del criterio de la sesion. Se avisa para que las filas que ya estaban
        // guardadas (que no la traen) puedan usar la misma.
        if (props.onEscalaAvance != null) {
            const conEscala = lista.filter((row) => row.idPuntuacionAvance > 0);
            props.onEscalaAvance(conEscala.length > 0 ? conEscala[0].idPuntuacionAvance : 0);
        }
    }, [])

    useEffect(() => {
        fetchRows(props.idTerapia, props.idPeriodo);
    }, [props.idTerapia, props.idPeriodo, fetchRows])

    // Los indicadores salen del plan de terapia y son los mismos para todas sus
    // filas. Si en el mismo periodo entran varios planes, manda el que pide mas
    // detalle: tapar un campo que hace falta deja el criterio incompleto.
    // Sin plan no hay nada que ocultar, asi que quedan en null.
    const detalle = useMemo(() => {
        if (rows.length === 0) return { detObjetivo: null, detCriterio: null };

        return {
            detObjetivo: rows.some((row) => row.detObjetivo !== false),
            detCriterio: rows.some((row) => row.detCriterio !== false),
        };
    }, [rows])

    useEffect(() => {
        if (props.onDetalle != null) {
            props.onDetalle(detalle.detObjetivo, detalle.detCriterio);
        }
    }, [detalle.detObjetivo, detalle.detCriterio])

    const agregar = (row) => {
        if (alAgregarCriterio == null) return;
        alAgregarCriterio({
            idModelo: row.idModelo,
            codigoModelo: row.codigoModelo,
            modelo: row.modelo,
            // El area es opcional en el detalle del plan: si no hay, el criterio
            // entra en blanco para que el terapeuta elija.
            idArea: row.idArea == null ? 0 : row.idArea,
            codigoArea: row.codigoArea,
            area: row.area,
            areaModelo: row.areaModelo || [],
            detObjetivo: detalle.detObjetivo,
            detCriterio: detalle.detCriterio,
            // Escala de avance de la cabecera del plan: es la que define los
            // grupos de puntuacion disponibles en el criterio.
            idPuntuacion: row.idPuntuacionAvance == null ? 0 : row.idPuntuacionAvance,
        });
    }

    // Sin terapia no hay nada que listar.
    if (!props.idTerapia || props.idTerapia <= 0) return null;

    return (
        <Grid container>
            <Grid item>
                <Typography variant="h7">
                    <Box sx={{ fontWeight: 'bold' }} padding={0}>
                        {"Plan de Terapia (" + (cargando ? "..." : rows.length) + ")"}
                    </Box>
                </Typography>
            </Grid>
            {/* Indicadores del plan de terapia: son de solo lectura, los cambia
                el mantenimiento del plan. Ademas son los que heredan los
                criterios que se crean con la flecha. */}
            <Grid item xs={12} sx={{ paddingTop: "4px" }}>
                <Grid container spacing={1}>
                    <Grid item>
                        <Typography variant="body2">
                            {"Detallar Objetivo: "}
                            <Box component="span" sx={{ fontWeight: "bold" }}>
                                {detalle.detObjetivo === null ? "(Sin plan)" : (detalle.detObjetivo ? "Si" : "No")}
                            </Box>
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="body2">
                            {"Detallar Criterio: "}
                            <Box component="span" sx={{ fontWeight: "bold" }}>
                                {detalle.detCriterio === null ? "(Sin plan)" : (detalle.detCriterio ? "Si" : "No")}
                            </Box>
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container>
                {rows.length === 0 && !cargando ? (
                    <Grid item xs={12} sx={{ paddingTop: "4px" }}>
                        <Typography variant="caption" color="text.secondary">
                            {"La terapia de la sesion no tiene areas en su plan para el periodo de la sesion."}
                        </Typography>
                    </Grid>
                ) : (
                    <Table size="small">
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow key={row.id + "-" + row.numero} sx={(theme) => ({
                                    backgroundColor: index % 2 === 1 ? theme.palette.action.selected : 'inherit',
                                    "&:last-child td, &:last-child th": { borderRadius: "0.5em" },
                                })}>
                                    <TableCell>
                                        <Grid container spacing={1} padding={0}>
                                            <Grid item xs={6} sm={3}>
                                                <Typography variant="body2">
                                                    {(row.codigoModelo || "") + " - " + (row.modelo || "")}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="body2">
                                                    {(row.codigoArea || "") + " " + (row.area || "")}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={2} sm={1}>
                                                <Tooltip title="Agregar criterio con este modelo y area">
                                                    <span>
                                                        <IconButton
                                                            aria-label={"agregar criterio de " + (row.area || "sin area")}
                                                            size="small"
                                                            disabled={isReadOnly}
                                                            onClick={() => { agregar(row); }}>
                                                            <IconArrowDown fontSize="small" color="primary" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Grid>
        </Grid>
    )
}

export default SessionPlanArea;
